import React, { useRef, useState } from "react";
import {
  Button,
  Stack,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { reportingApi } from "../api/reporting";
import { clientBrandingApi } from "../../../../../shared/api";
import { qk } from "../../../../../lib/queryKeys";

interface LogoUploadButtonProps {
  clientId: string | null;
  /** Current client logo URL (from branding), shown as a thumbnail. */
  currentLogoUrl: string | null;
}

/**
 * Uploads a client report logo and stores it against the client's branding, so
 * it becomes the default logo on this and future reports. Flow: presign PUT →
 * upload the file to S3 → PUT the public file_url onto client branding →
 * invalidate the branding query so slides pick it up immediately.
 *
 * Disabled for "all clients" (no client to store against).
 */
export const LogoUploadButton: React.FC<LogoUploadButtonProps> = ({
  clientId,
  currentLogoUrl,
}) => {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    if (!clientId) return;
    setUploading(true);
    setError(null);
    try {
      const { upload_url, file_url } = await reportingApi.createLogoUploadUrl({
        filename: file.name,
        content_type: file.type,
      });
      // Direct S3 PUT — bare axios (no app baseURL/credentials).
      await axios.put(upload_url, file, {
        headers: { "Content-Type": file.type },
      });
      // Store on both light and dark logo slots so slides (dark theme) use it.
      await clientBrandingApi.updateClientBranding(clientId, {
        logo_url: file_url,
        logo_dark_url: file_url,
        dark_logo_url: file_url,
      });
      qc.invalidateQueries({ queryKey: qk.clientBranding(clientId) });
    } catch (e) {
      setError("Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      {currentLogoUrl ? (
        <Box
          component="img"
          src={currentLogoUrl}
          alt="Client logo"
          sx={{
            height: 40,
            maxWidth: 160,
            objectFit: "contain",
            bgcolor: "#0B0F14",
            borderRadius: 1,
            p: 0.5,
          }}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          No client logo — PEAK4 branding only.
        </Typography>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = "";
        }}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={
          uploading ? <CircularProgress size={14} /> : <ImageIcon fontSize="small" />
        }
        onClick={() => inputRef.current?.click()}
        disabled={uploading || !clientId}
      >
        {currentLogoUrl ? "Replace logo" : "Upload client logo"}
      </Button>
      {!clientId && (
        <Typography variant="caption" color="text.secondary">
          Select a client to set its logo.
        </Typography>
      )}
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Stack>
  );
};
